import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, Plus, Sparkles } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, humanise } from '@/lib/format';
import {
  Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, LoadingBlock, Modal,
  MoneyInput, PageHeader, Select, Stat, Table, TableWrap, Td, Textarea, Th, useToast, statusTone,
} from '@/components/ui';
import type { Charge, Property, Lease, ChargeKind } from '@shared/types';

const KINDS: ChargeKind[] = ['rent', 'deposit', 'utility', 'late_fee', 'maintenance', 'other'];

export default function Charges() {
  const { currency, can } = useAuth();
  const toast = useToast();

  const [propertyId, setPropertyId] = useState('');
  const [status, setStatus] = useState('');
  const [outstandingOnly, setOutstandingOnly] = useState(true);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: properties } = useApi<Property[]>('/properties');
  const { data, loading, reload } = useApi<Charge[]>('/charges', {
    property_id: propertyId || undefined,
    status: status || undefined,
    outstanding: outstandingOnly,
  });

  const charges = data ?? [];
  const totals = charges.reduce(
    (acc, c) => ({
      billed: acc.billed + c.amount_cents - c.waived_cents,
      paid: acc.paid + (c.paid_cents ?? 0),
      balance: acc.balance + (c.balance_cents ?? 0),
    }),
    { billed: 0, paid: 0, balance: 0 },
  );

  async function generateAll() {
    setBusy(true);
    try {
      const res = await api.post<{ created: number; leases_processed: number }>('/leases/generate-all');
      toast.success(res.created > 0
        ? `${res.created} rent charge${res.created === 1 ? '' : 's'} created across ${res.leases_processed} leases`
        : 'Every lease is already up to date');
      setGenerating(false);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not generate rent');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rent ledger"
        subtitle="Everything tenants have been charged"
        action={can('manager') && (
          <>
            <Button icon={<Sparkles className="h-4 w-4" />} onClick={() => setGenerating(true)}>
              Generate rent
            </Button>
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              Add charge
            </Button>
          </>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Billed" value={money(totals.billed, currency)} hint="After waivers" />
        <Stat label="Paid" value={money(totals.paid, currency)} tone="ok" hint="Verified only" />
        <Stat
          label="Outstanding"
          value={money(totals.balance, currency)}
          tone={totals.balance > 0 ? 'danger' : 'ok'}
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-auto min-w-[180px]">
          <option value="">All properties</option>
          {(properties ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[150px]">
          <option value="">Any status</option>
          {['open', 'part_paid', 'paid', 'waived'].map((s) => (
            <option key={s} value={s}>{humanise(s)}</option>
          ))}
        </Select>
        <Button
          variant={outstandingOnly ? 'primary' : 'outline'}
          onClick={() => setOutstandingOnly((v) => !v)}
        >
          {outstandingOnly ? 'Showing unpaid only' : 'Showing everything'}
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : charges.length === 0 ? (
          <EmptyState
            icon={<Receipt className="h-6 w-6" />}
            title={outstandingOnly ? 'Nothing outstanding' : 'No charges yet'}
            message={outstandingOnly
              ? 'Every charge has been settled. Switch the filter to see the full history.'
              : 'Use "Generate rent" to raise this period\'s rent across all live leases.'}
            action={can('manager') && (
              <Button variant="primary" size="sm" onClick={() => setGenerating(true)}>Generate rent</Button>
            )}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Tenant</Th>
                  <Th>Charge</Th>
                  <Th>Due</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Paid</Th>
                  <Th align="right">Balance</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c) => {
                  const effectiveDue = c.effective_due_date ?? c.due_date;
                  const isOverdue = (c.balance_cents ?? 0) > 0 && new Date(effectiveDue) < new Date();
                  return (
                    <tr key={c.id} className="row-hover">
                      <Td>
                        <Link to={`/app/tenants/${c.tenant_id}`} className="font-medium hover:text-brand">
                          {c.tenant_name}
                        </Link>
                        <p className="text-xs text-faint truncate">
                          {c.room_name ? `${c.room_name} · ` : ''}{c.property_name}
                        </p>
                      </Td>
                      <Td>
                        <p className="text-muted">{c.description}</p>
                        <Badge tone="neutral" className="mt-1">{humanise(c.kind)}</Badge>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <span className={isOverdue ? 'text-danger font-medium' : 'text-muted'}>
                          {date(effectiveDue)}
                        </span>
                        {c.extended_to && <Badge tone="info" className="ml-1.5">Extended</Badge>}
                      </Td>
                      <Td align="right" className="tnum whitespace-nowrap">
                        {money(c.amount_cents, currency)}
                        {c.waived_cents > 0 && (
                          <span className="block text-xs text-ok">−{money(c.waived_cents, currency)}</span>
                        )}
                      </Td>
                      <Td align="right" className="tnum text-muted whitespace-nowrap">
                        {money(c.paid_cents ?? 0, currency)}
                      </Td>
                      <Td align="right" className="tnum font-semibold whitespace-nowrap">
                        <span className={(c.balance_cents ?? 0) > 0 ? (isOverdue ? 'text-danger' : '') : 'text-ok'}>
                          {money(c.balance_cents ?? 0, currency)}
                        </span>
                      </Td>
                      <Td><Badge tone={statusTone(c.status)}>{humanise(c.status)}</Badge></Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {creating && (
        <ChargeFormModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); reload(); }} />
      )}

      <ConfirmDialog
        open={generating}
        onClose={() => setGenerating(false)}
        onConfirm={() => void generateAll()}
        loading={busy}
        tone="primary"
        title="Generate rent charges?"
        confirmLabel="Generate"
        message={
          <>
            This raises any rent charges that are missing on live leases, up to today.
            Periods already billed are skipped, so running it twice is harmless.
          </>
        }
      />
    </div>
  );
}

function ChargeFormModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { currency } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [leaseId, setLeaseId] = useState('');
  const [kind, setKind] = useState<ChargeKind>('other');
  const [description, setDescription] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [dueDate, setDueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const { data: leases } = useApi<Lease[]>('/leases', { status: 'active' });

  async function submit() {
    if (!leaseId) { toast.error('Choose a tenant'); return; }
    if (!description.trim()) { toast.error('Describe what the charge is for'); return; }
    if (amountCents <= 0) { toast.error('Enter an amount'); return; }

    setBusy(true);
    try {
      await api.post('/charges', {
        lease_id: leaseId,
        kind,
        description: description.trim(),
        amount_cents: amountCents,
        due_date: dueDate,
        notes: notes || null,
      });
      toast.success('Charge added');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add the charge');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a charge"
      subtitle="For anything outside the regular rent"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Add charge</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Tenant" required>
          <Select value={leaseId} onChange={(e) => setLeaseId(e.target.value)}>
            <option value="">Choose a tenant…</option>
            {(leases ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.tenant_name} — {l.room_name ?? l.property_name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type">
            <Select value={kind} onChange={(e) => setKind(e.target.value as ChargeKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{humanise(k)}</option>)}
            </Select>
          </Field>
          <Field label="Due date" required>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>

        <Field label="Description" required>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Replacement window pane"
          />
        </Field>

        <Field label="Amount" required>
          <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
        </Field>

        <Field label="Notes">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
